import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Video, Phone, MapPin, Clock, ChevronLeft, ChevronRight, Check, Calendar as CalIcon, ArrowLeft } from 'lucide-react';
import { AawtaLogo } from '../components/AawtaLogo';
import { toast, Toaster } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${API_BASE}/api` });

const LOCATION_ICONS = { video: Video, phone: Phone, in_person: MapPin };
const LOCATION_LABELS = { video: 'Video call', phone: 'Phone call', in_person: 'In person' };

function detectTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
}

function fmtSlot(iso, tz) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  } catch {
    return new Date(iso).toLocaleTimeString();
  }
}

function fmtLongDate(d, tz) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz });
}

const STEPS = ['type', 'date', 'time', 'form', 'confirm'];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('type');
  const [mt, setMt] = useState(null);
  const [monthCursor, setMonthCursor] = useState(new Date());
  const [chosenDate, setChosenDate] = useState(null); // YYYY-MM-DD (in host tz)
  const [chosenTime, setChosenTime] = useState(null); // ISO
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [tz, setTz] = useState(detectTz());
  const [form, setForm] = useState({ attendee_name: '', attendee_email: '', attendee_phone: '', attendee_company: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    document.title = 'AAWTA Partnership Hub · Book a meeting';
    publicApi.get(`/public/book/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This booking link is not available.'));
  }, [slug]);

  const loadSlots = async (dateStr) => {
    if (!mt || !dateStr) return;
    setLoadingSlots(true);
    try {
      const r = await publicApi.get(`/public/book/${slug}/slots`, { params: { meeting_type_id: mt.id, date: dateStr } });
      setSlots(r.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (step === 'time' && chosenDate) loadSlots(chosenDate);
  }, [step, chosenDate, mt]);

  const submit = async () => {
    if (!form.attendee_name || !form.attendee_email || !chosenTime) return;
    setSubmitting(true);
    try {
      const r = await publicApi.post(`/public/book/${slug}`, {
        meeting_type_id: mt.id,
        start_at: chosenTime,
        attendee_timezone: tz,
        ...form,
      });
      setConfirmed(r.data);
      setStep('confirm');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not book that time. Please try another slot.');
    } finally {
      setSubmitting(false);
    }
  };

  const monthDays = useMemo(() => {
    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth();
    const first = new Date(y, m, 1);
    const start = first.getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < start; i++) arr.push(null);
    for (let d = 1; d <= days; d++) arr.push(new Date(y, m, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [monthCursor]);

  const isPastDay = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const workingDayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const hasWorkingHours = (d) => {
    if (!data) return false;
    const key = workingDayKeys[d.getDay()];
    return ((data.profile.working_hours || {})[key] || []).length > 0;
  };

  const isBlackout = (d) => (data?.profile.blackout_dates || []).includes(d.toISOString().slice(0, 10));

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AawtaLogo size={48} />
            <h1 className="text-navy font-heading text-xl font-bold mt-4">Booking link unavailable</h1>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-[#F8FAFC] p-10 text-slate-500">Loading…</div>;

  const { profile, meeting_types, host } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-magenta/5" data-testid="public-booking-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-4xl mx-auto p-4 md:p-10">
        <div className="mb-6 flex items-center justify-between">
          <AawtaLogo size={44} />
          <select
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="text-xs bg-white border border-slate-200 rounded-lg px-3 py-1.5"
            data-testid="tz-selector"
          >
            {[tz, profile.timezone, 'UTC'].filter((v, i, a) => a.indexOf(v) === i).map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-navy to-magenta text-white p-6 md:p-8">
            <div className="text-xs uppercase tracking-widest text-white/70 mb-2">Book a meeting</div>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold">{profile.display_name || host.name}</h1>
            {profile.headline && <div className="text-sm text-white/80 mt-1">{profile.headline}</div>}
            {profile.bio && <p className="text-sm text-white/70 mt-3 max-w-2xl">{profile.bio}</p>}
          </div>

          <div className="p-6 md:p-8">
            {/* Progress */}
            {step !== 'confirm' && (
              <div className="flex items-center gap-2 text-xs mb-6" data-testid="booking-step">
                {['Type', 'Date', 'Time', 'Details'].map((s, i) => {
                  const active = STEPS.indexOf(step) === i;
                  const done = STEPS.indexOf(step) > i;
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-magenta text-white' : 'bg-slate-200 text-slate-500'}`}>
                        {done ? <Check className="w-3 h-3" /> : i + 1}
                      </div>
                      <span className={`font-semibold ${active ? 'text-navy' : 'text-slate-400'}`}>{s}</span>
                      {i < 3 && <span className="text-slate-200">→</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Step: meeting type */}
            {step === 'type' && (
              <div className="space-y-3" data-testid="step-type">
                <div className="text-sm text-slate-500 mb-2">Pick a meeting type</div>
                {meeting_types.map((t) => {
                  const Icon = LOCATION_ICONS[t.location_type] || Video;
                  return (
                    <button
                      key={t.id}
                      onClick={() => { setMt(t); setStep('date'); }}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 hover:border-magenta hover:shadow-md transition text-left"
                      data-testid={`mt-choice-${t.id}`}
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: (t.color || '#E4007C') + '15', color: t.color || '#E4007C' }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-navy">{t.name}</div>
                        {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                        <div className="text-xs text-slate-600 mt-1 flex items-center gap-3">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.duration_minutes} min</span>
                          <span>{LOCATION_LABELS[t.location_type]}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Step: date */}
            {step === 'date' && (
              <div data-testid="step-date">
                <button onClick={() => setStep('type')} className="text-xs text-slate-500 mb-4 flex items-center gap-1 hover:text-magenta"><ArrowLeft className="w-3 h-3" /> Change meeting type</button>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs text-slate-500">{mt.name} · {mt.duration_minutes} min</div>
                    <div className="font-heading text-lg font-bold text-navy">Pick a date</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))} data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></Button>
                    <div className="text-sm font-semibold text-navy px-2 min-w-[140px] text-center">
                      {monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))} data-testid="month-next"><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {monthDays.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    const disabled = isPastDay(d) || !hasWorkingHours(d) || isBlackout(d);
                    const isToday = d.toDateString() === new Date().toDateString();
                    return (
                      <button
                        key={i}
                        disabled={disabled}
                        onClick={() => { setChosenDate(dateStr); setStep('time'); }}
                        className={`aspect-square rounded-lg text-sm font-semibold transition ${
                          disabled
                            ? 'text-slate-300 cursor-not-allowed line-through decoration-slate-200'
                            : 'text-navy hover:bg-magenta hover:text-white cursor-pointer'
                        } ${isToday && !disabled ? 'ring-2 ring-magenta ring-offset-1' : ''}`}
                        data-testid={`day-${dateStr}`}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step: time */}
            {step === 'time' && (
              <div data-testid="step-time">
                <button onClick={() => setStep('date')} className="text-xs text-slate-500 mb-4 flex items-center gap-1 hover:text-magenta"><ArrowLeft className="w-3 h-3" /> Change date</button>
                <div className="mb-4">
                  <div className="text-xs text-slate-500">{mt.name} · {mt.duration_minutes} min</div>
                  <div className="font-heading text-lg font-bold text-navy">{fmtLongDate(new Date(chosenDate + 'T12:00:00'), tz)}</div>
                  <div className="text-xs text-slate-500 mt-1">Times shown in <b>{tz}</b></div>
                </div>
                {loadingSlots ? (
                  <div className="text-sm text-slate-500 text-center py-8">Loading available times…</div>
                ) : slots.length === 0 ? (
                  <div className="text-sm text-slate-500 text-center py-8">No times available on this day. Try another date.</div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {slots.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setChosenTime(s); setStep('form'); }}
                        className="border-2 border-slate-100 hover:border-magenta hover:bg-magenta hover:text-white rounded-lg py-2.5 text-sm font-semibold text-navy transition"
                        data-testid={`slot-${s}`}
                      >
                        {fmtSlot(s, tz)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step: form */}
            {step === 'form' && (
              <div data-testid="step-form" className="space-y-3">
                <button onClick={() => setStep('time')} className="text-xs text-slate-500 mb-4 flex items-center gap-1 hover:text-magenta"><ArrowLeft className="w-3 h-3" /> Change time</button>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 mb-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-navy">
                    <CalIcon className="w-4 h-4 text-magenta" />
                    {new Date(chosenTime).toLocaleString('en-US', { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz })}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{mt.name} · {mt.duration_minutes} min · {LOCATION_LABELS[mt.location_type]}</div>
                </div>
                <div><Label className="text-xs">Full name *</Label><Input value={form.attendee_name} onChange={(e) => setForm({ ...form, attendee_name: e.target.value })} data-testid="form-name" required /></div>
                <div><Label className="text-xs">Email *</Label><Input type="email" value={form.attendee_email} onChange={(e) => setForm({ ...form, attendee_email: e.target.value })} data-testid="form-email" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Phone</Label><Input value={form.attendee_phone} onChange={(e) => setForm({ ...form, attendee_phone: e.target.value })} data-testid="form-phone" /></div>
                  <div><Label className="text-xs">Company</Label><Input value={form.attendee_company} onChange={(e) => setForm({ ...form, attendee_company: e.target.value })} data-testid="form-company" /></div>
                </div>
                <div><Label className="text-xs">What would you like to discuss?</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" /></div>
                <Button
                  onClick={submit}
                  disabled={submitting || !form.attendee_name || !form.attendee_email}
                  className="w-full bg-magenta hover:bg-magenta/90 text-white h-11 mt-2"
                  data-testid="submit-booking"
                >
                  {submitting ? 'Booking…' : 'Confirm booking'}
                </Button>
              </div>
            )}

            {/* Step: confirmed */}
            {step === 'confirm' && confirmed && (
              <div className="text-center py-6" data-testid="step-confirm">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="font-heading text-2xl font-extrabold text-navy">You&apos;re booked!</h2>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  A confirmation email is on its way to <b>{form.attendee_email}</b>. Add it to your calendar so you don&apos;t miss it.
                </p>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 max-w-sm mx-auto mt-6 text-left">
                  <div className="text-xs text-slate-500">Meeting</div>
                  <div className="font-semibold text-navy">{confirmed.meeting_type} with {confirmed.host}</div>
                  <div className="text-xs text-slate-500 mt-2">When</div>
                  <div className="font-semibold text-navy">{new Date(confirmed.start_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz })}</div>
                  {confirmed.location?.details && (
                    <>
                      <div className="text-xs text-slate-500 mt-2">Where</div>
                      <div className="font-semibold text-navy break-words">{confirmed.location.details}</div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-[11px] text-slate-400 mt-6">Powered by AAWTA Partnership Hub</div>
      </div>
    </div>
  );
}
